import React, { useEffect, useState, useRef } from "react";

export const RightPanelFigures = ({ editor, isShow, isOpen }) => {
  const [figures, setFigures] = useState([]);

  useEffect(() => {
    if (editor?.dom && isShow === "figures" && isOpen) {
      const allFigs = editor.dom.select("fig");
      const allXrefs = editor.dom.select("xref");

      if (allFigs && allFigs.length > 0) {
        const newFigures = [];
        allFigs.forEach((item, index) => {
          const id = item.getAttribute("id") || `fig-gen-${index}`;
          const label = item.querySelector("label")?.textContent || id || "Figure";
          const title = item.querySelector("title")?.textContent || item.querySelector("caption")?.textContent || "";
          
          // Get the actual graphic element (img, svg, or raw innerHTML fallback)
          const graphicEl = item.querySelector("img, svg, graphic");
          const graphicHtml = graphicEl ? graphicEl.outerHTML : "";
          const img = item.querySelector("img");
          const imgSrc = img ? img.getAttribute("src") : "";

          const xrefCount = Array.from(allXrefs).filter((xref) => {
            const refType = xref.getAttribute("ref-type") || "";
            if (refType !== "fig") return false;
            const href = xref.getAttribute("href") || "";
            const rid = xref.getAttribute("rid") || "";
            
            const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            
            const idNum = id.replace(/\D/g, "");
            const hrefNum = href.replace(/\D/g, "");
            const ridNum = rid.replace(/\D/g, "");
            
            const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
            const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
            
            return matchesStr || matchesNum;
          }).length;

          newFigures.push({
            id,
            label,
            title,
            graphicHtml,
            imgSrc,
            count: xrefCount,
          });
        });
        setFigures(newFigures);
      } else {
        setFigures([]);
      }
    }
  }, [isShow, isOpen, editor]);

  const selectFigInEditor = (id) => {
    if (!editor || !id) return;
    const dom = editor.dom;
    const queryEle = dom.select(`fig[id="${id}"]`)[0];
    if (queryEle) {
      editor.selection.select(queryEle);
      queryEle.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const xrefIndicesRef = useRef({});

  const handleClick = (id) => {
    const editorBody = editor?.dom;
    if (!editorBody) return;

    const allXrefs = editorBody.select("xref");
    const xrefs = Array.from(allXrefs).filter((xref) => {
      const refType = xref.getAttribute("ref-type") || "";
      if (refType !== "fig") return false;
      const href = xref.getAttribute("href") || "";
      const rid = xref.getAttribute("rid") || "";
      
      const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      
      const idNum = id.replace(/\D/g, "");
      const hrefNum = href.replace(/\D/g, "");
      const ridNum = rid.replace(/\D/g, "");
      
      const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
      const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
      
      return matchesStr || matchesNum;
    });

    if (!xrefs.length) return;

    if (xrefIndicesRef.current[id] === undefined) {
      xrefIndicesRef.current[id] = 0;
    }

    const currentIndex = xrefIndicesRef.current[id] % xrefs.length;
    const targetElement = xrefs[currentIndex];

    if (targetElement) {
      editor.selection.select(targetElement);
      targetElement.focus();
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    xrefIndicesRef.current[id] = (xrefIndicesRef.current[id] + 1) % xrefs.length;
  };

  if (isShow !== "figures") return null;

  return (
    <div className="w-full flex flex-col gap-4">
      {figures.length > 0 ? (
        <div className="flex flex-col gap-3">
          {figures.map((item, i) => (
            <div
              key={i}
              className="relative w-full flex flex-col cursor-pointer bg-white rounded-md hover:bg-panelrefbg border border-grey py-2 px-[6px] hover:border-panelref"
            >
              <div
                onClick={() => selectFigInEditor(item.id)}
                className="flex flex-col w-full gap-[6px] pb-6"
              >
                <span className="font-semibold text-sm text-[#4F46E5]">{item.label}</span>
                {item.title && (
                  <p className="text-[13px] text-gray-600 line-clamp-3">{item.title}</p>
                )}
                {item.graphicHtml ? (
                  <div 
                    className="w-full flex justify-center mt-2 bg-[#F8F9FA] rounded border border-grey p-2 overflow-hidden max-h-[160px] fig-preview"
                    dangerouslySetInnerHTML={{ __html: item.graphicHtml }}
                  />
                ) : item.imgSrc ? (
                  <div className="w-full flex justify-center mt-2 bg-[#F8F9FA] rounded border border-grey overflow-hidden max-h-[120px]">
                    <img
                      src={item.imgSrc}
                      alt={item.label}
                      className="object-contain max-h-[120px]"
                    />
                  </div>
                ) : null}
              </div>
              <div className="flex justify-end z-9 absolute bottom-[8px] right-[8px]">
                <span
                  onClick={() => handleClick(item.id)}
                  className="text-[12px] text-green hover:text-red font-medium"
                >
                  cited{`(${item.count})`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 py-8 text-sm">No figures found in document</div>
      )}
    </div>
  );
};
